import {
  ApprovalChainsAttachmentsDrawer,
  ApprovalChainsRejectDrawer,
  ApprovalsConfigsDrawer,
  ApprovalsEditDrawer,
} from '@approvals/components';
import {
  CreateCandidateDrawer,
  UpdateCandidateDrawer,
} from '@clinician-profile/components';
import { MemberViewDrawer } from '@identity/components';
import { ScheduleTeamReviewWorkerDrawer } from '@load-balancing/components';
import { BillingRatesDrawer } from 'works/legacy/components/billing-rates-drawer';
import { CancelAssignmentDrawer } from 'works/legacy/components/staffing-request-assignment-cancel';
import { StaffingRequestReviewSubmissionDrawer } from 'works/legacy/pages/supplier/staffing-requests/staffing-request/staffing-request-review-submission-drawer';
import {
  AdminScheduleCodeDrawer,
  CopyShiftTimesDrawer,
  CreateShiftDrawer,
  IncentiveRatesViewDrawer,
  MessageDetailDrawer,
  SendShiftDrawer,
  SetWorkloadDrawer,
  ShiftRequestDetailsDrawer,
  ShiftsCalendarDetailsDrawer,
  ShiftsCalendarFiltersDrawer,
  UnitShiftGapTagCreateDrawer,
  UnitShiftGapTagUpdateDrawer,
  WorkforceGroupCreateDrawer,
  WorkforceGroupOrgBusinessUpdateDrawer,
  WorkforceGroupUpdateDrawer,
  WorkerCreateDrawer,
} from '@scheduling/components';
import {
  ApplicationRejectionDrawer,
  OfferRejectionDrawer,
  SubmittalPacketChangeRequestDrawer,
  SubmittalPacketPreviewerDrawer,
  OfferConfirmationDrawer,
  OfferPreviewDrawer,
} from '@applicant-flow/components';
import { DialogsMembershipPermissionsType } from './_dialogs-permissions-query';
import {
  OrganizationFacilityAddDrawer,
  OrganizationFacilityEditDrawer,
  OrganizationUnitEditDrawer,
  RatesLongTermViewDrawer,
  SuppliersManagementTiersDrawer,
  SuppliersManagementViewDrawer,
  SuppliersManagementReinviteDrawer,
} from '@institutions/components';
import {
  ComplianceItemAddAttachmentDrawer,
  ComplianceItemDrawer,
  ComplianceItemRejectionDrawer,
  ComplianceItemUpsertDrawerV2,
} from '@onboarding/components';
import {
  DisputeCreateDrawer,
  DisputeCreateMissingLineDrawer,
  DisputeViewDrawer,
} from '@pay-bill/components';
import {
  AddHourBlockDrawer,
  HourBlockEditDrawer,
  HourBlockRestoreDrawer,
  HourBlockReviewDrawer,
} from '@timekeeping/components';
import { DocumentPreviewDrawer } from '@common/components';
import {
  ExtensionOfferRejectionDrawer,
  ExtensionOfferPreviewDrawer,
  ExtensionOfferDrawer,
  ProfileStatusSetupApplicationDrawer,
  ProfileStatusSetupAssignmentDrawer,
} from '@working-experience/components';
import {
  StaffingRequestTemplateDetailsDrawer,
  StaffingRequestTemplateDrawer,
  EditStaffingRequestDrawer,
  EditStaffingRequestOnApprovalDrawer,
  StaffingRequestDetailsDrawer,
} from '@jobs/components';

/**
 * Register all the global dialogs here (like drawers, modals, etc).
 * Mind that dialog keys must be written in kebab-case and ordered alphabetically within the correct domain section.
 * Also, prefer creating friendly keys, since it will be exposed to the user.
 * More info in ./dialogs-readme.md
 */
export const DIALOGS_MAP = {
  // Applicant Flow
  'review-submission-drawer': {
    Component: StaffingRequestReviewSubmissionDrawer,
  },
  'submittal-packet-change-request-drawer': {
    Component: SubmittalPacketChangeRequestDrawer,
    canShow: (permissions: DialogsMembershipPermissionsType) =>
      permissions.canSubmitSubmittalPacketChangeRequest,
  },
  'submittal-packet-previewer-drawer': {
    Component: SubmittalPacketPreviewerDrawer,
  },
  'application-rejection-drawer': {
    Component: ApplicationRejectionDrawer,
  },
  'offer-rejection-drawer': {
    Component: OfferRejectionDrawer,
    canShow: ({ isSupplierUser }: DialogsMembershipPermissionsType) =>
      isSupplierUser,
  },
  'offer-confirmation-drawer': {
    Component: OfferConfirmationDrawer,
    canShow: ({ isSupplierUser }: DialogsMembershipPermissionsType) =>
      isSupplierUser,
  },
  'offer-preview-drawer': {
    Component: OfferPreviewDrawer,
  },
  'extension-offer-preview-drawer': {
    Component: ExtensionOfferPreviewDrawer,
  },

  // Approvals
  'approvals-edit-drawer': {
    Component: ApprovalsEditDrawer,
  },
  'approvals-options-drawer': {
    Component: ApprovalsConfigsDrawer,
  },
  'approvals-attachments-drawer': {
    Component: ApprovalChainsAttachmentsDrawer,
  },
  'approvals-chain-reject-drawer': {
    Component: ApprovalChainsRejectDrawer,
  },

  // Clinician Profile
  'create-candidate-drawer': {
    Component: CreateCandidateDrawer,
  },
  'update-candidate-drawer': {
    Component: UpdateCandidateDrawer,
  },

  // Common
  'document-preview-drawer': {
    Component: DocumentPreviewDrawer,
  },

  // Identity
  'user-view-drawer': { Component: MemberViewDrawer },

  // Institutions
  'organization-facility-add-drawer': {
    Component: OrganizationFacilityAddDrawer,
  },
  'organization-facility-edit-drawer': {
    Component: OrganizationFacilityEditDrawer,
  },
  'organization-unit-edit-drawer': {
    Component: OrganizationUnitEditDrawer,
  },
  'rates-long-term-view-drawer': {
    Component: RatesLongTermViewDrawer,
  },
  'supplier-view-drawer': {
    Component: SuppliersManagementViewDrawer,
  },
  'supplier-management-tiers-drawer': {
    Component: SuppliersManagementTiersDrawer,
  },
  'suppliers-management-reinvite-drawer': {
    Component: SuppliersManagementReinviteDrawer,
  },

  // Jobs
  'billing-rates-drawer': {
    Component: BillingRatesDrawer,
  },
  'cancel-assignment-drawer': {
    Component: CancelAssignmentDrawer,
  },
  'staffing-request-details-drawer': {
    Component: StaffingRequestDetailsDrawer,
  },
  'staffing-request-template-details-drawer': {
    Component: StaffingRequestTemplateDetailsDrawer,
  },
  'staffing-request-template-drawer': {
    Component: StaffingRequestTemplateDrawer,
  },
  'edit-staffing-request-drawer': {
    Component: EditStaffingRequestDrawer,
  },
  'edit-staffing-request-on-approval-drawer': {
    Component: EditStaffingRequestOnApprovalDrawer,
  },

  // Load Balancing
  'schedule-worker-view-drawer': {
    Component: ScheduleTeamReviewWorkerDrawer,
  },

  // Onboarding
  'compliance-item-drawer': {
    Component: ComplianceItemDrawer,
  },
  'compliance-item-rejection-drawer': {
    Component: ComplianceItemRejectionDrawer,
  },
  'compliance-item-update-drawer-v2': {
    Component: ComplianceItemUpsertDrawerV2,
  },
  'compliance-item-add-attachment-drawer': {
    Component: ComplianceItemAddAttachmentDrawer,
  },

  // Pay Bill
  'dispute-create-drawer': {
    Component: DisputeCreateDrawer,
  },
  'dispute-create-missing-line-drawer': {
    Component: DisputeCreateMissingLineDrawer,
  },
  'dispute-view-drawer': {
    Component: DisputeViewDrawer,
  },

  // Scheduling
  'schedule-code-drawer': {
    Component: AdminScheduleCodeDrawer,
    canShow: ({
      canShowAdminScheduleCodesTab,
    }: DialogsMembershipPermissionsType) => canShowAdminScheduleCodesTab,
  },
  'create-shift-drawer': {
    Component: CreateShiftDrawer,
    canShow: ({
      canCreateShiftGaps,
      canEditShiftGaps,
    }: DialogsMembershipPermissionsType) =>
      canCreateShiftGaps && canEditShiftGaps,
  },
  'copy-shift-times-drawer': {
    Component: CopyShiftTimesDrawer,
  },
  'incentive-rates-view-drawer': {
    Component: IncentiveRatesViewDrawer,
  },
  'shift-message-detail-drawer': {
    Component: MessageDetailDrawer,
    canShow: ({ canShowShiftTargeting }: DialogsMembershipPermissionsType) =>
      canShowShiftTargeting,
  },
  'send-shift-drawer': {
    Component: SendShiftDrawer,
    canShow: ({ canShowShiftTargeting }: DialogsMembershipPermissionsType) =>
      canShowShiftTargeting,
  },
  'set-workload-drawer': {
    Component: SetWorkloadDrawer,
    canShow: ({
      canCreateShiftGaps,
      canEditShiftGaps,
    }: DialogsMembershipPermissionsType) =>
      canCreateShiftGaps && canEditShiftGaps,
  },
  'shifts-calendar-details-drawer': {
    Component: ShiftsCalendarDetailsDrawer,
  },
  'shifts-calendar-filters-drawer': {
    Component: ShiftsCalendarFiltersDrawer,
  },
  'shifts-request-details-drawer': {
    Component: ShiftRequestDetailsDrawer,
  },
  'unit-shift-gap-tag-create-drawer': {
    Component: UnitShiftGapTagCreateDrawer,
  },
  'unit-shift-gap-tag-update-drawer': {
    Component: UnitShiftGapTagUpdateDrawer,
  },
  'workforce-group-create-drawer': {
    Component: WorkforceGroupCreateDrawer,
  },
  'workforce-group-org-business-update-drawer': {
    Component: WorkforceGroupOrgBusinessUpdateDrawer,
  },
  'workforce-group-update-drawer': {
    Component: WorkforceGroupUpdateDrawer,
  },
  'worker-create-drawer': {
    Component: WorkerCreateDrawer,
  },

  // Timekeeping
  'timekeeping-add-hour-block-drawer': {
    Component: AddHourBlockDrawer,
  },
  'timekeeping-hour-block-edit-drawer': {
    Component: HourBlockEditDrawer,
  },
  'timekeeping-hour-block-restore-drawer': {
    Component: HourBlockRestoreDrawer,
  },
  'timekeeping-hour-block-review-drawer': {
    Component: HourBlockReviewDrawer,
  },

  // Working Experience
  'extension-offer-rejection-drawer': {
    Component: ExtensionOfferRejectionDrawer,
  },
  'extension-offer-drawer': {
    Component: ExtensionOfferDrawer,
  },
  'profile-status-setup-application-drawer': {
    Component: ProfileStatusSetupApplicationDrawer,
  },
  'profile-status-setup-assignment-drawer': {
    Component: ProfileStatusSetupAssignmentDrawer,
  },
};

export type DialogsMapKeyType = keyof typeof DIALOGS_MAP;

export const DIALOGS_MAP_KEYS = Object.keys(DIALOGS_MAP);
